import { Component, computed, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { animate, query, stagger, style, transition, trigger } from '@angular/animations';
import { UntypedFormGroup } from '@angular/forms';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  CardTokenQuery,
  CheckoutService,
  config,
  ITEM_CART_TYPE,
  SUMMARY_PRICE_KIND,
  LoggingService,
  MarketplaceV2Service,
  ModelDisplay,
  ModelMetaData,
  PaymentService,
  PropertyTypeCode,
  UnifiedCheckoutAddress,
  UnifiedCheckoutAddressRequest,
  UnifiedCheckoutResponse,
  UnifiedCheckoutSummary,
  UnifiedCheckoutSummaryItem,
  UnifiedCheckoutCompleteRequest,
  MarketplaceFacade,
  AddressService,
  CardTokenService,
  PAYMENT_PROVIDER,
  ProviderActionKind,
  ProviderName,
} from '@fgb/core';
import { ModelFormHelperService } from '@fgb/portal-component-library/src/lib/shared/forms/model-meta-data-form';
import { finalize } from 'rxjs';

/**
 * Address field validation patterns, per the Address Validations wiki page
 * (Portal.wiki/2683). The accented ranges (À-Ö, Ø-ö, ø-ÿ) cover the Latin-1
 * letters used in international names and places.
 */
const PATTERNS = {
  /** Letters, spaces, commas, periods, apostrophes and hyphens. */
  NAME: "^[a-zA-Z ,.'-]+$",
  /** Letters (incl. accents), digits, . , ' - ( ) and spaces, for "1st Avenue". */
  STREET: "^[A-Za-zÀ-ÖØ-öø-ÿ0-9.,'\\-()\\s]+$",
  /** Words of letters separated by spaces or hyphens, e.g. "Nord-Pas-de-Calais". */
  PLACE: '^[A-Za-zÀ-ÖØ-öø-ÿ]+(?:[\\s-][A-Za-zÀ-ÖØ-öø-ÿ]+)*$',
  /** Alphanumeric, spaces and hyphens, 3-10 characters. */
  POST_CODE: '^[A-Za-z0-9\\s-]{3,10}$',
  /** Optional country code, optional area code, then the subscriber number. */
  PHONE: '^\\+?[0-9]{1,4}(\\s|[-.])?(\\(?[0-9]{1,5}\\)?)(\\s|[-.])?[0-9]{4,10}$',
  EMAIL: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,3}$',
};

@Component({
  selector: 'fgb-unified-checkout-page',
  templateUrl: './unified-checkout-page.component.html',
  styleUrl: './unified-checkout-page.component.scss',
  animations: [
    trigger('fadeInUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(24px)' }),
        animate('500ms ease', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
    trigger('fadeInUpDelayed', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(24px)' }),
        animate('500ms 300ms ease', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
    trigger('staggerChildren', [
      transition(':enter', [
        query(
          ':scope > *:not(.payment-section)',
          [
            style({ opacity: 0, transform: 'translateY(24px)' }),
            stagger(300, [animate('500ms ease', style({ opacity: 1, transform: 'translateY(0)' }))]),
          ],
          { optional: true }
        ),
      ]),
    ]),
  ],
})
export class UnifiedCheckoutPageComponent implements OnInit, OnDestroy {
  @Input() mode: 'add-card' | 'checkout' = 'checkout';
  billingForm: UntypedFormGroup;
  isEditingBillingAddress = true;
  savingAddress = false;
  readonly isLoadingCheckout = signal(true);
  readonly paymentDivId = 'paymentDiv';
  readonly isMobileSummaryOpen = signal(false);
  readonly isPaymentInitialised = signal(false);
  readonly isPaymentFormLoading = signal(false);
  readonly showNewCardPayment = signal(false);
  private loadedForAddress: string | null = null;
  readonly resultsUrl = '/payment/checkout/results';
  readonly successUrl = '/payment/checkout/success';
  readonly failureUrl = '/payment/checkout/failure';
  readonly prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  readonly itemCartType = ITEM_CART_TYPE;
  readonly summaryPriceKind = SUMMARY_PRICE_KIND;

  private checkoutService = inject(CheckoutService);
  private cardTokenService = inject(CardTokenService);
  private addressService = inject(AddressService);
  private marketplaceService = inject(MarketplaceV2Service);
  private cardTokenQuery = inject(CardTokenQuery);
  private modelFormHelper = inject(ModelFormHelperService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private paymentService = inject(PaymentService);
  private logService = inject(LoggingService);
  private marketplaceFacade = inject(MarketplaceFacade);
  /** The club's configured card payment provider, used by the add-card flow. */
  private paymentProvider = inject(PAYMENT_PROVIDER);
  readonly checkoutResponse = signal<UnifiedCheckoutResponse | null>(null);
  readonly items = computed<UnifiedCheckoutSummaryItem[]>(() =>
    this.checkoutService.toSummaryItems(this.checkoutResponse()?.Basket)
  );
  readonly totalItems = computed(() => this.items().reduce((count, item) => count + item.quantity, 0));
  readonly cardInfo = toSignal(this.cardTokenQuery.selectDefaultCard(), { initialValue: null });
  /**
   * Whether the member has a saved card to pay with, which decides if the linked-card row shows.
   * A frozen card cannot be charged, so it is not offered as a one-press route.
   */
  readonly useCardToken = computed(() => {
    const card = this.cardInfo();
    return !!card?.CardNumber && !card.Frozen;
  });
  /** Translation key prefix for the address field labels. */
  readonly wscPrefix = 'billing.address.form';
  private orderId: string | null = null;
  readonly subtotal = computed(() => this.checkoutResponse()?.Summary?.TotalMerchant ?? 0);
  readonly estimatedTaxes = computed(() => this.checkoutResponse()?.Summary?.TaxMerchant ?? 0);
  readonly total = computed(() => this.checkoutResponse()?.Summary?.BasketTotalMerchant ?? 0);
  readonly totalPoints = computed(() => this.checkoutResponse()?.Summary?.TotalPoints ?? 0);
  readonly hasPoints = computed(() => this.totalPoints() > 0);
  readonly hasMoneyTotal = computed(() => this.total() > 0);
  readonly isFreeBasket = computed(() => !this.hasMoneyTotal() && !this.hasPoints());
  readonly hasMoneySubtotal = computed(() => this.subtotal() > 0);
  readonly isProcessingLinkedCard = signal(false);
  readonly provider = ProviderName;

  /**
   * Context for the wallet express checkout (Apple Pay, Google Pay, PayPal,
   * Paze).
   *
   * The component reads the success and failure routes from here rather than
   * from inputs of its own, and navigates to them itself once a wallet
   * settles - so these are router paths, not the absolute urls the
   * checkout/complete body carries.
   *
   * useCardToken is false: a wallet supplies its own payment credential, so
   * there is no saved token involved. The basket lines come from the checkout
   * response, which is the server's own view of what is being paid for.
   */
  readonly expressCheckoutContext = computed(() => ({
    orderId: this.checkoutResponse()?.OrderId ?? '',
    useCardToken: false,
    successUrl: this.successUrl,
    failureUrl: this.failureUrl,
  }));

  /**
   * Field definitions for the billing address form. The paired fields are Half
   * so each pair shares a row, and Email Address is Full to span the width, as
   * in the design. Country and Region use their dedicated property types so the
   * region list follows the chosen country.
   */
  readonly addressModels: ModelMetaData[] = [
    this.addressModel('FirstName', PropertyTypeCode.String, 1, ModelDisplay.Half, true, PATTERNS.NAME),
    this.addressModel('Surname', PropertyTypeCode.String, 2, ModelDisplay.Half, true, PATTERNS.NAME),
    this.addressModel('Street', PropertyTypeCode.String, 3, ModelDisplay.Half, true, PATTERNS.STREET),
    this.addressModel('Town', PropertyTypeCode.String, 4, ModelDisplay.Half, true, PATTERNS.PLACE),
    this.addressModel('County', PropertyTypeCode.Region, 5),
    this.addressModel('Country', PropertyTypeCode.Country, 6),
    this.addressModel('PostCode', PropertyTypeCode.PostCode, 7, ModelDisplay.Half, true, PATTERNS.POST_CODE),
    this.addressModel('HomeNumber', PropertyTypeCode.String, 8, ModelDisplay.Half, true, PATTERNS.PHONE),
    this.addressModel('EmailAddress1', PropertyTypeCode.String, 9, ModelDisplay.Full, true, PATTERNS.EMAIL),
  ];

  constructor(private location: Location) {
    this.billingForm = this.initializeAddressForm();
  }

  /** Branches on the route's mode: an order to pay for, or a card to add. */
  ngOnInit(): void {
    if (!this.isCheckout) {
      this.initAddCard();
      return;
    }

    this.initCheckout();
  }

  /** Tears the provider down on the way out - see destroyPayment for why. */
  ngOnDestroy(): void {
    this.destroyPayment();
  }

  /**
   * Pays with the member's saved card token.
   *
   * There is no provider form and no redirect here - the server charges the
   * stored token and answers with the outcome, so this navigates to the
   * success or failure page itself rather than handing a url to a provider.
   * That is why the body carries successUrl rather than resultsUrl: nothing
   * comes back through the results page to be verified.
   *
   * The re-entry guard matters because the button stays mounted behind the
   * processing modal.
   */
  payWithLinkedCard(): void {
    if (this.isProcessingLinkedCard()) {
      return;
    }

    this.isProcessingLinkedCard.set(true);

    const body: UnifiedCheckoutCompleteRequest = {
      orderId: this.orderId,
      successUrl: config.portalUrl + this.successUrl,
      failureUrl: config.portalUrl + this.failureUrl,
      useCardToken: this.useCardToken(),
      saveCardAsToken: false,
    };
    this.marketplaceService
      .completeCheckout(this.orderId, body)
      .pipe(
        finalize(() => {
          this.isProcessingLinkedCard.set(false);
          this.marketplaceFacade.clearCache();
        })
      )
      .subscribe({
        next: (response) => {
          if (response.Data.Success) {
            this.router.navigate([this.successUrl], { queryParams: { orderId: response.Data.OrderId } });
          } else {
            this.router.navigate([this.failureUrl]);
          }
        },
        error: () => {
          this.router.navigate([this.failureUrl]);
        },
      });
  }

  /**
   * Reveals the payment section and fetches the provider's card form.
   *
   * The form is loaded from here rather than when the address is confirmed, so
   * nothing is fetched until the member asks to pay by card. The billing form
   * is rechecked even though the button is hidden while it is invalid: the
   * click is the thing that starts a payment, so it does not rely on the
   * template alone to keep an incomplete address out.
   */
  payWithNewCard(): void {
    if (this.billingForm.invalid) {
      return;
    }

    if (this.total() > 0) {
      this.showNewCardPayment.set(true);
    }
    this.loadPaymentForm();
  }

  /**
   * Reopens the billing address form from the confirmed address card.
   *
   * Any payment already mounted is torn down: the address drives the tax, so
   * changing it invalidates the amount the provider was given.
   */
  editBillingAddress(): void {
    this.isEditingBillingAddress = true;
    this.showNewCardPayment.set(false);
  }

  /** Returns to wherever checkout was entered from. Hidden on mobile. */
  goBack(): void {
    this.location.back();
  }

  /** Expands or collapses the mobile summary. */
  toggleMobileSummary(): void {
    this.isMobileSummaryOpen.update((open) => !open);
  }

  /**
   * Confirms the billing address, then hands off to whichever flow is running.
   *
   * Both branches need a valid address first, so the validity check lives here
   * rather than being repeated in each of them.
   */
  continueToPayment(): void {
    if (this.billingForm.invalid) {
      this.billingForm.markAllAsTouched();
      return;
    }

    if (!this.isCheckout) {
      this.saveTopUpAddress();
      return;
    }

    this.updateCheckoutAddress();
  }

  /**
   * Add-card branch: mounts the provider's form for storing a new card token.
   *
   * The provider comes from PAYMENT_PROVIDER, which the club configures once,
   * so it is always set and there is nothing to guard against here.
   */
  saveTopUpAddress(): void {
    this.savingAddress = true;
    this.isPaymentFormLoading.set(true);
    this.isEditingBillingAddress = false;
    this.showNewCardPayment.set(true);

    this.addressService
      .postTopUpAddress(this.toAddressRequest() as any)
      .then(() => {
        if (this.isPaymentInitialised()) {
          this.savingAddress = false;
          this.isPaymentFormLoading.set(false);
          return;
        }

        return this.cardTokenService
          .addNewCardToken(
            this.paymentProvider,
            config.portalUrl + '/cardtoken/result',
            this.paymentDivId,
            config.portalUrl + '/cardtoken/result'
          )
          .then(() => {
            this.savingAddress = false;
            this.isPaymentInitialised.set(true);
            this.isPaymentFormLoading.set(false);
          });
      })
      .catch((error) => {
        this.savingAddress = false;
        this.isPaymentFormLoading.set(false);
        this.isEditingBillingAddress = true;
        this.showNewCardPayment.set(false);
        this.logService.error('[unified-checkout] failed to mount the add-card form', error);
      });
  }

  /**
   * Checkout branch: saves the billing address against the order. The server
   * recalculates tax from the address, so the response replaces the current
   * summary rather than merging into it.
   */
  updateCheckoutAddress(): void {
    const orderId = this.checkoutResponse()?.OrderId;
    if (!orderId) {
      return;
    }

    if (this.isPaymentInitialised() && this.addressKey() === this.loadedForAddress) {
      this.isEditingBillingAddress = false;
      // The member already chose to pay by new card, so return them to the payment panel rather
      // than offering that choice again.
      this.showNewCardPayment.set(true);
      return;
    }

    this.savingAddress = true;

    this.marketplaceService.updateCheckoutAddress(orderId, this.toAddressRequest()).subscribe({
      next: (summary: UnifiedCheckoutSummary) => {
        this.savingAddress = false;
        this.checkoutResponse.update((order) => (order ? { ...order, Summary: summary } : order));
        this.isEditingBillingAddress = false;
        // Once the member has committed to paying by new card, editing the address should bring
        // them back to the payment panel, not re-offer the choice they already made.
        if (this.isPaymentInitialised()) {
          this.showNewCardPayment.set(true);
        }
      },
      error: () => {
        this.savingAddress = false;
      },
    });
  }

  /**
   * Add-card mode: seeds the billing form from the member's stored top-up
   * address. There is no order and no summary in this mode.
   */
  private initAddCard(): void {
    this.addressService.getTopUpAddress().subscribe((address) => {
      this.billingForm = this.initializeAddressForm(address as any);
      this.isLoadingCheckout.set(false);
    });
  }

  /**
   * Checkout mode: resumes the order named in the url, or creates one from the
   * current basket. Resuming is what makes a reload or a return trip from a
   * provider land on the same order instead of charging for a second one.
   */
  private initCheckout(): void {
    const orderId = this.route.snapshot.queryParamMap.get('orderId');

    const checkout$ = orderId
      ? this.marketplaceService.getCheckout(orderId)
      : this.marketplaceService.checkout(this.checkoutService.checkoutBasket());

    checkout$.subscribe({
      next: (response: UnifiedCheckoutResponse) => {
        this.applyCheckoutResponse(response, !this.orderId);
        this.isLoadingCheckout.set(false);
      },
      error: (error) => {
        this.isLoadingCheckout.set(false);
        this.logService.error('Failed to load the checkout order', error);
      },
    });
  }

  /**
   * Loads the provider's card form.
   *
   * Called from payWithNewCard, so nothing is fetched until the member asks to
   * pay by card. The guards below still stand on their own: an invalid address
   * or a missing order leaves the panel empty rather than mounting a form for
   * an amount that cannot be billed.
   *
   * PaymentService.process() finds the mount div with a synchronous
   * getElementById, so the div has to already be in the DOM when this runs.
   * The payment section is hidden with [hidden] rather than @if for exactly
   * that reason - an @if would remove the div and there would be nothing to
   * mount into.
   */
  private loadPaymentForm(): void {
    if (this.isPaymentInitialised()) {
      return;
    }

    if (this.isEditingBillingAddress || this.billingForm.invalid) {
      return;
    }

    const orderId = this.checkoutResponse()?.OrderId;
    if (!orderId) {
      return;
    }

    this.isPaymentFormLoading.set(true);
    this.loadedForAddress = this.addressKey();

    const body = {
      orderId,
      successUrl: config.portalUrl + this.resultsUrl,
      failureUrl: config.portalUrl + this.failureUrl,
      useCardToken: false,
      saveCardAsToken: false,
    };
    this.marketplaceService.completeCheckout(orderId, body).subscribe({
      next: (response) => {
        const paymentResponse = response.Data;

        if (!paymentResponse?.ProviderActionInformation) {
          this.isPaymentFormLoading.set(false);
          this.logService.error('[unified-checkout] no provider response from checkout/complete', response);
          return;
        }

        if (paymentResponse?.ProviderActionInformation.ActionKind === ProviderActionKind.None) {
          this.router.navigate([this.successUrl], { queryParams: { orderId: paymentResponse.OrderId } });
          return;
        }

        this.paymentService.createServerCalculatedPayment({
          paymentResponse,
          divName: this.paymentDivId,
          callbackUrl: this.resultsUrl,
          successUrl: this.successUrl,
          failureUrl: this.failureUrl,
          summary: this.checkoutResponse()?.Summary,
          basket: this.items(),
        });
        this.isPaymentInitialised.set(true);
        this.isPaymentFormLoading.set(false);
      },
      error: (error) => {
        this.isPaymentFormLoading.set(false);
        this.logService.error('[unified-checkout] failed to load the payment form', error);
      },
    });
  }

  /**
   * The current billing address as a comparable string, for deciding whether a
   * loaded card form is still valid for it.
   */
  private addressKey(): string {
    return JSON.stringify(this.toAddressRequest());
  }

  /** Maps the form's MemberDetails-style controls to the endpoint's camelCase body. */
  private toAddressRequest(): UnifiedCheckoutAddressRequest {
    const form = this.billingForm.value;
    return {
      firstName: form.FirstName,
      surname: form.Surname,
      street: form.Street,
      town: form.Town,
      county: form.County,
      country: form.Country,
      postcode: form.PostCode,
      homeNumber: form.HomeNumber,
      emailAddress: form.EmailAddress1,
    };
  }

  /** Builds one ModelMetaData entry, to keep the addressModels list readable. */
  private addressModel(
    propertyName: string,
    propertyTypeCode: PropertyTypeCode,
    ordinal: number,
    displayStatus: ModelDisplay = ModelDisplay.Half,
    isRequired = true,
    validateExpression: string | null = null
  ): ModelMetaData {
    return {
      PropertyName: propertyName,
      PropertyTypeCode: propertyTypeCode,
      Ordinal: ordinal,
      DisplayStatus: displayStatus,
      IsRequired: isRequired,
      Validate: !!validateExpression,
      ValidateExpression: validateExpression,
    };
  }

  /**
   * Tears down a mounted provider component.
   *
   * The template hides the payment section, which removes the host div, but the
   * provider's view is attached to the ApplicationRef rather than to this
   * component's, so Angular does not destroy it. Leaving it attached leaks the
   * hosted-field iframes and the provider's injected scripts.
   */
  private destroyPayment(): void {
    if (this.isPaymentInitialised()) {
      this.paymentService.destroyPaymentComponent();
      this.isPaymentInitialised.set(false);
    }

    // The panel is empty again, so nothing is loaded and nothing is on its way.
    this.isPaymentFormLoading.set(false);
  }

  /** Settles the page onto a loaded order: summary, billing form, and url. */
  private applyCheckoutResponse(response: UnifiedCheckoutResponse, isNewOrder: boolean): void {
    this.checkoutResponse.set(response);

    // Seed the form from the billing address the order was created with.
    this.billingForm = this.initializeAddressForm(response.Basket?.BillingAddress);

    // A complete address needs no editing, so start on the summary card.
    this.isEditingBillingAddress = this.billingForm.invalid;

    // Keep the order id in the URL so a reload reuses this order.
    if (isNewOrder && response.OrderId) {
      this.orderId = response.OrderId;
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { orderId: response.OrderId },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  /**
   * Builds the billing form, optionally seeded from a saved address.
   *
   * Two fields are renamed on the way in: the form's controls follow the
   * MemberDetails property names the shared model-form expects (PostCode,
   * EmailAddress1), while the address entity spells them Postcode and
   * EmailAddress. toAddressRequest reverses this on the way out.
   */
  private initializeAddressForm(address?: UnifiedCheckoutAddress): UntypedFormGroup {
    const data = address
      ? {
          ...address,
          PostCode: address.Postcode,
          EmailAddress1: address.EmailAddress,
        }
      : {};

    return this.modelFormHelper.buildForm(this.addressModels, data);
  }

  /** Address line of the confirmed billing address card. */
  get billingAddressLine(): string {
    const form = this.billingForm.value;
    return [`${form.FirstName ?? ''} ${form.Surname ?? ''}`.trim(), form.Street, form.Town, form.County, form.Country]
      .filter(Boolean)
      .join(', ');
  }

  /** Phone and email line of the confirmed billing address card. */
  get billingContactLine(): string {
    const form = this.billingForm.value;
    return [form.HomeNumber, form.EmailAddress1].filter(Boolean).join('  ');
  }

  /** True in checkout mode, false when the page is being used to add a card. */
  get isCheckout(): boolean {
    return this.mode === 'checkout';
  }
}
